import { useEffect, useMemo, useState } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { LoadingBackdrop } from 'components/common'
import {
  Mode,
  Paths,
  DEFAULT_COUNTRY_ID,
  DriverPurchaseCodes,
  VatCategoryType,
  DEFAULT_CURRENCY_ID,
} from 'constants/index'
import {
  CreateAndUpdateDriverDtoInput,
  DriverLicenseType,
  DriverType,
  PaymentTerm,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useGetDriverXeroCodesQuery,
} from 'generated/graphql'
import { usePermission } from 'providers'

import DriverFormControls from './DriverFormControls'
import UpdateDriver from './UpdateDriver'
import validationSchema from './ValidationSchema'

interface IProps {
  driverId: number | null
  mode: number | undefined
}

const DriverDetails = (props: IProps) => {
  const { driverId, mode } = props
  const history = useHistory()
  const [showWarningMessage, setShowWarningMessage] = useState(false)

  const [selectedVatCategoryId, setSelectedVatCategoryId] = useState(String(VatCategoryType.t0))
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(DEFAULT_CURRENCY_ID)

  const [isAllowedToViewXeroPurchaseCodes] = usePermission('ViewEditXeroPurchaseCode')

  const { data: driverXeroCodesData, loading: driverXeroCodeLoading } = useGetDriverXeroCodesQuery({
    skip: !isAllowedToViewXeroPurchaseCodes,
  })

  const driverXeroCodeOptions = useMemo(() => {
    if (
      !driverXeroCodeLoading &&
      driverXeroCodesData?.driverXeroCodes &&
      isAllowedToViewXeroPurchaseCodes
    ) {
      return driverXeroCodesData?.driverXeroCodes.map((vc) => ({
        label: vc.xeroCode,
        value: vc.xeroCode,
        vatCategoryId: Number(vc.vatCategory?.id),
      }))
    }
    return []
  }, [driverXeroCodesData, driverXeroCodeLoading, isAllowedToViewXeroPurchaseCodes])

  const schema = useMemo(() => {
    return validationSchema(selectedVatCategoryId, selectedCurrencyId, driverXeroCodeOptions)
  }, [selectedVatCategoryId, selectedCurrencyId, driverXeroCodeOptions])

  const methods = useForm<CreateAndUpdateDriverDtoInput>({
    shouldUnregister: true,
    resolver: joiResolver(schema, {
      context: {
        isAllowedToViewXeroPurchaseCodes,
      },
    }),
    defaultValues: {
      name: '',
      driverType: DriverType.Contract,
      xeroCodeId: '',
      courierExchangeMemberId: '',
      xeroPurchaseCode:
        mode === Mode.Create ? DriverPurchaseCodes.nonVatRegisteredPurchaseCode : '',
      postcode: '',
      firstName: '',
      lastName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      countryId: DEFAULT_COUNTRY_ID,
      email: '',
      telephoneNumber: '',
      alternativeTelephoneNumber: '',
      startDate: new Date(),
      paymentTerm: PaymentTerm.MonthFromEndOfInvoiceMoth,
      invoiceEmail: '',
      eoriNumber: '',
      currencyId: '',
      licenseType: DriverLicenseType.Standard,
      endorsements: '',
      lastPaid: null,
      vatCategoryId: String(VatCategoryType.t0),
      bankName: '',
      bankAccountNumber: '',
      bankAccountHolderName: '',
      internationalBankAccountNumber: '',
      vatIdNumber: '',
      bankSortCode: '',
      employeeReference: '',
      isActive: true,
      isOnboardingCompleted: false,
      swift: '',
      companyRegistrationNumber: '',
    },
  })

  const { control, handleSubmit, clearErrors, reset } = methods

  const watchedVatCategoryId = useWatch({
    control,
    name: 'vatCategoryId',
  })

  const watchedCurrencyId = useWatch({
    control,
    name: 'currencyId',
  })

  // Mutations
  const [createDriver, { loading: creatingDriver }] = useCreateDriverMutation({
    onCompleted: (data) => {
      if (!data?.createDriver?.id) {
        toast.error('Driver was not created. Please try again later.')
        return
      }

      toast.success(`Driver ${data?.createDriver.name} was successfully created.`)
      history.push(Paths.drivers.updateWithId(data?.createDriver.id))
    },
  })
  const [updateDriver, { loading: updatingDriver }] = useUpdateDriverMutation({
    onCompleted: (data) => {
      if (!data?.updateDriver?.id) {
        toast.error('Driver was not updated. Please try again later.')
        return
      }
      toast.success(`Driver ${data?.updateDriver.name} was successfully updated.`)

      // Enable post-update user validation message
      setShowWarningMessage(true)
    },
  })

  const handleWarning = (warning: string) => {
    if (showWarningMessage && warning) {
      toast.warning(warning)
      setShowWarningMessage(false)
    }
  }

  useEffect(() => {
    if (watchedVatCategoryId && watchedVatCategoryId !== selectedVatCategoryId) {
      setSelectedVatCategoryId(watchedVatCategoryId)
    }
    clearErrors('vatIdNumber')
  }, [watchedVatCategoryId, selectedVatCategoryId, clearErrors])

  useEffect(() => {
    if (watchedCurrencyId && watchedCurrencyId !== selectedCurrencyId) {
      setSelectedCurrencyId(String(watchedCurrencyId))
      clearErrors(['bankAccountNumber', 'bankSortCode', 'bankAccountHolderName'])
    }
  }, [watchedCurrencyId, selectedCurrencyId, clearErrors])

  useEffect(() => {
    clearErrors('xeroPurchaseCode')
  }, [selectedVatCategoryId, driverXeroCodeOptions, clearErrors])

  const onSubmit: SubmitHandler<CreateAndUpdateDriverDtoInput> = (data) => {
    const {
      countryId,
      licenseType,
      xeroCodeId,
      courierExchangeMemberId,
      publicLiabilityExpirationDate,
      ...restData
    } = data

    const normalizedVars = {
      countryId: countryId ? Number(countryId) : null,
      licenseType: licenseType || DriverLicenseType.Standard, // TODO: License type nullable
      xeroCodeId: xeroCodeId ? Number(xeroCodeId) : null,
      courierExchangeMemberId: courierExchangeMemberId || null,
      publicLiabilityExpirationDate: publicLiabilityExpirationDate
        ? publicLiabilityExpirationDate
        : undefined,
    }

    if (mode === Mode.Create) {
      createDriver({
        variables: {
          input: {
            id: null,
            ...restData,
            ...normalizedVars,
          },
        },
      })
    } else {
      updateDriver({
        variables: {
          input: {
            id: String(driverId!),
            ...restData,
            ...normalizedVars,
          },
        },
      })
      reset()
    }
  }

  const isProcessing = creatingDriver || updatingDriver

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {mode === Mode.Create ? (
            <DriverFormControls mode={Mode.Create} isProcessing={isProcessing} />
          ) : (
            <UpdateDriver
              driverId={driverId!}
              isProcessing={isProcessing}
              onWarningAvailable={handleWarning}
            />
          )}
        </form>
      </FormProvider>
      <LoadingBackdrop loading={isProcessing} />
    </div>
  )
}

export default DriverDetails
